import styled, { css } from 'styled-components';

export const ArticleShell = styled.div<{ mode: 'page' | 'modal' }>`
  color: ${(props) => props.theme.colors.onSurface};
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem;
`;

export const ArticleHero = styled.section<{ compact?: boolean }>`
  border-bottom: 1px solid ${(props) => `${props.theme.colors.onSurface}14`};
  color: ${(props) => props.theme.colors.onSurface};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: ${(props) => (props.compact ? '0 0 1rem' : '0 0 1.15rem')};

  h1,
  h2 {
    margin: 0;
    font-size: ${(props) => (props.compact ? '1.3rem' : '1.6rem')};
    line-height: 1.25;
  }

  p {
    margin: 0;
    color: ${(props) => props.theme.colors.text};
    font-size: 0.96rem;
    line-height: 1.7;
    max-width: 72ch;
  }
`;

export const ArticleEyebrow = styled.span`
  align-self: flex-start;
  border-bottom: 2px solid ${(props) => `${props.theme.colors.primary}35`};
  color: ${(props) => props.theme.colors.primary};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 0 0 0.2rem;
  text-transform: uppercase;
`;

export const ArticleSection = styled.section`
  border-top: 1px solid ${(props) => `${props.theme.colors.onSurface}10`};
  padding-top: 1rem;

  h3 {
    color: ${(props) => props.theme.colors.primary};
    font-size: 1rem;
    margin: 0 0 0.75rem;
  }

  p {
    line-height: 1.65;
    margin: 0;
  }
`;

const listStyles = css`
  display: grid;
  gap: 0.55rem;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    color: ${(props) => props.theme.colors.text};
    line-height: 1.65;
    padding-left: 1.2rem;
    position: relative;
  }

  li::before {
    color: ${(props) => `${props.theme.colors.primary}cc`};
    content: '•';
    font-size: 1rem;
    left: 0;
    position: absolute;
    top: 0.05rem;
  }

  strong {
    color: ${(props) => props.theme.colors.primary};
  }
`;

export const ArticleList = styled.ul`
  ${listStyles}
`;

export const ArticleChecklist = styled.ul`
  ${listStyles}

  li::before {
    color: ${(props) => props.theme.colors.positive};
    content: '✓';
    font-weight: 700;
  }
`;

export const ArticleCallout = styled.div<{ tone?: 'info' | 'warning' }>`
  background: ${(props) => (props.tone === 'warning'
    ? `${props.theme.colors.warning}08`
    : `${props.theme.colors.primary}06`)};
  border: 1px solid ${(props) => (props.tone === 'warning'
    ? `${props.theme.colors.warning}20`
    : `${props.theme.colors.primary}18`)};
  border-left-width: 4px;
  border-left-color: ${(props) => (props.tone === 'warning'
    ? props.theme.colors.warning
    : props.theme.colors.primary)};
  border-radius: 0.75rem;
  line-height: 1.65;
  padding: 0.9rem 1rem;
`;

export const ArticleMediaFrame = styled.div<{ cleanMode?: boolean }>`
  background: ${(props) => (props.cleanMode
    ? 'transparent'
    : `${props.theme.colors.primary}04`)};
  border: ${(props) => (props.cleanMode
    ? 'none'
    : `1px dashed ${props.theme.colors.primary}30`)};
  padding: ${(props) => (props.cleanMode ? '0' : '1rem')};
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  h4,
  p {
    margin: 0;
  }

  h4 {
    color: ${(props) => props.theme.colors.primary};
  }

  p {
    color: ${(props) => props.theme.colors.text};
    line-height: 1.55;
  }
`;

export const ArticleTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
`;

export const ArticleTag = styled.span`
  color: ${(props) => props.theme.colors.text};
  font-size: 0.8rem;
  font-weight: 500;

  &::before {
    color: ${(props) => `${props.theme.colors.primary}aa`};
    content: '#';
    margin-right: 0.15rem;
  }
`;

export const ArticleTable = styled.table`
  border-collapse: collapse;
  margin: 0.5rem 0;
  width: auto;
  min-width: 180px;

  th,
  td {
    border: 1px solid ${(props) => `${props.theme.colors.onSurface}18`};
    color: ${(props) => props.theme.colors.text};
    font-size: 0.92rem;
    line-height: 1.5;
    padding: 0.45rem 1rem;
    text-align: left;
  }

  th {
    background: ${(props) => `${props.theme.colors.primary}0c`};
    color: ${(props) => props.theme.colors.primary};
    font-weight: 600;
  }

  tr:nth-child(even) td {
    background: ${(props) => `${props.theme.colors.onSurface}04`};
  }
`;
